import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect } from 'services/commonQuery';
import api from 'services/api';

/**
 * 시스템 운영 — 기관등록
 * API: hr_tenant + cm_tenant, 관리자 hr_sys_user + cm_user_account (로그인 가능)
 */

export const TENANT_STORAGE_KEY = 'erp.master.tenant';
export const TENANT_HISTORY_PREFIX = 'erp.master.tenantHistory';

export const TENANT_STATUS_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

/** 이력 변경유형 */
export const TENANT_CHANGE_TYPE = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  ADMIN: 'ADMIN',
  SEAL: 'SEAL',
  CLOSE: 'CLOSE',
};

export const TENANT_CHANGE_TYPE_LABEL = {
  CREATE: '신규',
  UPDATE: '수정',
  ADMIN: '관리자계정',
  SEAL: '직인',
  CLOSE: '사용중지',
};

export function createTenantUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `TN-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function tenantChangeTypeLabel(code) {
  return TENANT_CHANGE_TYPE_LABEL[code] || code || '';
}

/** 숫자만 추출 (사업자·법인번호 저장 형식) */
export function stripDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

/** 사업자등록번호 표시 포맷 xxx-xx-xxxxx (입력 진행 중이면 있는 만큼만) */
export function formatBizRegNo(value) {
  const d = stripDigits(value).slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

/** 법인등록번호 표시 포맷 xxxxxx-xxxxxxx */
export function formatCorpRegNo(value) {
  const d = stripDigits(value).slice(0, 13);
  if (d.length <= 6) return d;
  return `${d.slice(0, 6)}-${d.slice(6)}`;
}

function tenantHistoryKey(tenantUid) {
  return `${TENANT_HISTORY_PREFIX}|${tenantUid || ''}`;
}

/** 기존 BE 시드(V3·V11)와 동일 기관을 초기 등록 */
export function buildSampleTenants() {
  return [
    {
      tenantUid: createTenantUid(),
      tenantCode: 'DEMO-LFO',
      tenantNm: '데모 지방출연기관',
      fiscalTerm: 1,
      foundedYear: '',
      gccCode: '',
      businessRegNo: '',
      corpRegNo: '',
      address: '',
      ceoNm: '',
      sealImage: '',
      sealFileName: '',
      brandMainColor: '#2563EB',
      adminId: 'admin',
      adminNm: '관리자',
      adminPwd: '',
      useYn: 'Y',
      sortOrder: 10,
      remark: 'BE V3 시드 기관',
      mod: '',
    },
    {
      tenantUid: createTenantUid(),
      tenantCode: 'GNK',
      tenantNm: 'GNK 운영기관',
      fiscalTerm: 1,
      foundedYear: '',
      gccCode: '',
      businessRegNo: '',
      corpRegNo: '',
      address: '',
      ceoNm: '',
      sealImage: '',
      sealFileName: '',
      brandMainColor: '#0D9488',
      adminId: 'admin',
      adminNm: '관리자',
      adminPwd: '',
      useYn: 'Y',
      sortOrder: 20,
      remark: 'BE V11 시드 기관',
      mod: '',
    },
  ];
}

function loadRaw() {
  try {
    const raw = localStorage.getItem(TENANT_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : null;
  } catch {
    return null;
  }
}

/** 직인 이관용 — 동기 localStorage만 (API 목록과 무관) */
export function loadRawTenantsForSeal() {
  return loadRaw() || [];
}

function statusToUseYn(status) {
  return String(status || '').toUpperCase() === 'ACTIVE' ? 'Y' : 'N';
}

function mapApiTenant(row) {
  const tenantId = row.tenantId != null ? String(row.tenantId) : '';
  return {
    ...row,
    tenantId,
    tenantUid: tenantId || row.tenantUid || createTenantUid(),
    tenantCode: row.tenantCode || '',
    _origTenantCode: row.tenantCode || '',
    tenantNm: row.tenantNm || '',
    businessRegNo: formatBizRegNo(row.businessRegNo),
    corpRegNo: formatCorpRegNo(row.corpRegNo),
    gccCode: row.gccCode || '',
    address: row.address || '',
    ceoNm: row.ceoNm || '',
    adminId: row.adminId || 'admin',
    adminNm: row.adminNm || '관리자',
    adminPwd: '',
    useYn: row.useYn === 'Y' || row.useYn === 'N' ? row.useYn : statusToUseYn(row.status),
    sortOrder: row.sortOrder ?? 10,
    remark: row.remark || '',
    mod: '',
  };
}

export async function loadTenants() {
  if (isBdApiEnabled()) {
    const rows = await commonSelect('TenantRegister.selectTenantList', { listAll: 'Y' });
    return (rows || []).map(mapApiTenant);
  }
  const existing = loadRaw();
  if (existing && existing.length) {
    return existing.map((r) => ({
      ...r,
      tenantUid: r.tenantUid || createTenantUid(),
      businessRegNo: formatBizRegNo(r.businessRegNo),
      corpRegNo: formatCorpRegNo(r.corpRegNo),
      mod: '',
    }));
  }
  const seeded = buildSampleTenants();
  saveTenants(seeded);
  seeded.forEach((r) => {
    appendTenantHistory(r.tenantUid, {
      changeType: TENANT_CHANGE_TYPE.CREATE,
      changeAt: new Date().toISOString(),
      changeBy: 'system',
      memo: '초기 시드 등록',
      snapshot: snapshotTenant(r),
    });
  });
  return seeded;
}

function toTenantSaveRow(row) {
  return {
    tenantId: row.tenantId || null,
    tenantUid: row.tenantUid,
    tenantCode: String(row.tenantCode || '').trim(),
    tenantNm: String(row.tenantNm || '').trim(),
    businessRegNo: stripDigits(row.businessRegNo),
    corpRegNo: stripDigits(row.corpRegNo),
    gccCode: row.gccCode || '',
    address: row.address || '',
    ceoNm: row.ceoNm || '',
    orgType: row.orgType || 'LFO',
    legalRegime: row.legalRegime || 'LOCAL',
    adminId: String(row.adminId || '').trim(),
    adminNm: String(row.adminNm || '').trim() || '관리자',
    adminPwd: row.adminPwd || '',
    useYn: row.useYn || 'Y',
    remark: row.remark || '',
    mod: row.mod,
  };
}

export async function saveTenantsApi(rows) {
  const dirty = (rows || []).filter((r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D'));
  if (!dirty.length) {
    return loadTenants();
  }
  const res = await api.post('/system/tenant/save', {
    rows: dirty.map(toTenantSaveRow),
  });
  const data = res?.data || {};
  if (data.success === false || (data.Msg && String(data.Msg).toLowerCase() !== 'success')) {
    throw new Error(data.Msg || '기관 저장에 실패했습니다.');
  }
  return loadTenants();
}

export function saveTenants(rows) {
  const list = (rows || []).map((r) => ({
    ...r,
    tenantUid: r.tenantUid || createTenantUid(),
    // DB(저장소)에는 하이픈 없이 숫자만 저장
    businessRegNo: stripDigits(r.businessRegNo),
    corpRegNo: stripDigits(r.corpRegNo),
  }));
  localStorage.setItem(
    TENANT_STORAGE_KEY,
    JSON.stringify({ rows: list, savedAt: new Date().toISOString() })
  );
  return list;
}

/** 이력 스냅샷 — 직인 원문·비밀번호는 이력에 남기지 않음 */
export function snapshotTenant(row) {
  if (!row) return {};
  return {
    tenantCode: row.tenantCode || '',
    tenantNm: row.tenantNm || '',
    fiscalTerm: row.fiscalTerm ?? '',
    foundedYear: row.foundedYear ?? '',
    gccCode: row.gccCode || '',
    businessRegNo: stripDigits(row.businessRegNo),
    corpRegNo: stripDigits(row.corpRegNo),
    address: row.address || '',
    ceoNm: row.ceoNm || '',
    brandMainColor: row.brandMainColor || '',
    sealFileName: row.sealFileName || '',
    hasSeal: row.sealImage ? 'Y' : 'N',
    adminId: row.adminId || '',
    adminNm: row.adminNm || '',
    useYn: row.useYn || 'Y',
    sortOrder: row.sortOrder ?? '',
    remark: row.remark || '',
  };
}

export function loadTenantHistory(tenantUid) {
  if (!tenantUid) return [];
  try {
    const raw = localStorage.getItem(tenantHistoryKey(tenantUid));
    if (!raw) return [];
    const data = JSON.parse(raw);
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    return rows
      .map((r) => ({
        ...r,
        changeTypeNm: tenantChangeTypeLabel(r.changeType),
        changeAtDisp: formatDateTime(r.changeAt),
      }))
      .sort((a, b) => String(b.changeAt || '').localeCompare(String(a.changeAt || '')));
  } catch {
    return [];
  }
}

export function appendTenantHistory(tenantUid, entry) {
  if (!tenantUid || !entry) return;
  const prev = (() => {
    try {
      const raw = localStorage.getItem(tenantHistoryKey(tenantUid));
      if (!raw) return [];
      const data = JSON.parse(raw);
      return Array.isArray(data?.rows) ? data.rows : [];
    } catch {
      return [];
    }
  })();
  const next = [
    {
      histId: createTenantUid(),
      changeType: entry.changeType || TENANT_CHANGE_TYPE.UPDATE,
      changeAt: entry.changeAt || new Date().toISOString(),
      changeBy: entry.changeBy || '',
      memo: entry.memo || '',
      snapshot: entry.snapshot || {},
    },
    ...prev,
  ];
  localStorage.setItem(
    tenantHistoryKey(tenantUid),
    JSON.stringify({ tenantUid, rows: next, savedAt: new Date().toISOString() })
  );
}

/** 저장 전후 비교 → 이력 적재 */
export async function persistTenantsWithHistory(nextRows, { userId } = {}) {
  const prevList = isBdApiEnabled() ? await loadTenants() : loadRaw() || [];
  const prevByUid = new Map(prevList.map((r) => [r.tenantUid, r]));
  const list = (nextRows || []).map((r) => ({
    ...r,
    tenantUid: r.tenantUid || createTenantUid(),
  }));
  const byUid = new Map(list.map((r) => [r.tenantUid, r]));
  let historyTouched = 0;
  const now = new Date().toISOString();
  const by = userId || '';

  list.forEach((row) => {
    const prev = prevByUid.get(row.tenantUid);
    if (row.mod === 'D') {
      if (prev) {
        appendTenantHistory(row.tenantUid, {
          changeType: TENANT_CHANGE_TYPE.CLOSE,
          changeAt: now,
          changeBy: by,
          memo: '기관 삭제',
          snapshot: snapshotTenant(prev),
        });
        historyTouched += 1;
      }
      return;
    }
    if (!prev) {
      appendTenantHistory(row.tenantUid, {
        changeType: TENANT_CHANGE_TYPE.CREATE,
        changeAt: now,
        changeBy: by,
        memo: `기관 신규 등록 (관리자 ${row.adminId || '-'})`,
        snapshot: snapshotTenant(row),
      });
      historyTouched += 1;
      return;
    }
    const closed = prev.useYn === 'Y' && row.useYn === 'N';
    const adminChanged =
      String(prev.adminId || '') !== String(row.adminId || '') ||
      String(prev.adminNm || '') !== String(row.adminNm || '') ||
      String(prev.adminPwd || '') !== String(row.adminPwd || '');
    const sealChanged =
      String(prev.sealImage || '') !== String(row.sealImage || '') ||
      String(prev.sealFileName || '') !== String(row.sealFileName || '');
    const otherChanged =
      JSON.stringify(snapshotTenant({ ...prev, adminId: '', adminNm: '', sealFileName: '', sealImage: '' })) !==
      JSON.stringify(snapshotTenant({ ...row, adminId: '', adminNm: '', sealFileName: '', sealImage: '' }));

    if (closed) {
      appendTenantHistory(row.tenantUid, {
        changeType: TENANT_CHANGE_TYPE.CLOSE,
        changeAt: now,
        changeBy: by,
        memo: '사용여부 N(중지)',
        snapshot: snapshotTenant(row),
      });
      historyTouched += 1;
    } else if (otherChanged) {
      appendTenantHistory(row.tenantUid, {
        changeType: TENANT_CHANGE_TYPE.UPDATE,
        changeAt: now,
        changeBy: by,
        memo: '기관정보 수정',
        snapshot: snapshotTenant(row),
      });
      historyTouched += 1;
    }
    if (adminChanged) {
      appendTenantHistory(row.tenantUid, {
        changeType: TENANT_CHANGE_TYPE.ADMIN,
        changeAt: now,
        changeBy: by,
        memo: `관리자 계정 변경 (${row.adminId || '-'})`,
        snapshot: snapshotTenant(row),
      });
      historyTouched += 1;
    }
    if (sealChanged) {
      appendTenantHistory(row.tenantUid, {
        changeType: TENANT_CHANGE_TYPE.SEAL,
        changeAt: now,
        changeBy: by,
        memo: row.sealImage ? `직인 등록 (${row.sealFileName || '이미지'})` : '직인 삭제',
        snapshot: snapshotTenant(row),
      });
      historyTouched += 1;
    }
  });

  // 그리드에서 물리 삭제된 기관
  prevList.forEach((prev) => {
    if (!byUid.has(prev.tenantUid)) {
      appendTenantHistory(prev.tenantUid, {
        changeType: TENANT_CHANGE_TYPE.CLOSE,
        changeAt: now,
        changeBy: by,
        memo: '기관 삭제',
        snapshot: snapshotTenant(prev),
      });
      historyTouched += 1;
    }
  });

  const saved = isBdApiEnabled()
    ? await saveTenantsApi(list)
    : saveTenants(list.map((r) => ({ ...r, mod: '' })));
  return { saved, historyTouched };
}

/** 조회 필터 */
export function filterTenants(rows, { tenantCode, tenantNm, useYn } = {}) {
  return (rows || []).filter((r) => {
    if (useYn && r.useYn !== useYn) return false;
    if (tenantCode && !String(r.tenantCode || '').toLowerCase().includes(String(tenantCode).trim().toLowerCase())) {
      return false;
    }
    if (tenantNm && !String(r.tenantNm || '').includes(String(tenantNm).trim())) return false;
    return true;
  });
}

export function validateTenants(rows) {
  const list = rows || [];
  const codes = new Set();
  for (const r of list) {
    if (r.mod === 'D') continue;
    const code = String(r.tenantCode || '').trim();
    if (!code) return { ok: false, message: '기관코드는 필수입니다.' };
    const isNew = r.mod === 'A' || !r.tenantId;
    if (!isNew && r._origTenantCode && String(r._origTenantCode) !== code) {
      return {
        ok: false,
        message: `저장된 기관코드는 변경할 수 없습니다. (${r._origTenantCode})`,
      };
    }
    if (!/^[A-Za-z0-9-]+$/.test(code)) {
      return { ok: false, message: `기관코드 ${code}: 영문·숫자·하이픈만 사용할 수 있습니다.` };
    }
    if (!String(r.tenantNm || '').trim()) {
      return { ok: false, message: `기관코드 ${code}: 기관명은 필수입니다.` };
    }
    const upper = code.toUpperCase();
    if (codes.has(upper)) return { ok: false, message: `기관코드 중복: ${code}` };
    codes.add(upper);
    if (!String(r.adminId || '').trim()) {
      return { ok: false, message: `기관코드 ${code}: 초기 관리자 아이디를 입력하세요.` };
    }
    if (isNew && !String(r.adminPwd || '').trim()) {
      return { ok: false, message: `기관코드 ${code}: 관리자 비밀번호를 입력하세요. (로그인에 사용)` };
    }
    const biz = stripDigits(r.businessRegNo);
    if (biz && biz.length !== 10) {
      return { ok: false, message: `기관코드 ${code}: 사업자번호는 숫자 10자리입니다. (xxx-xx-xxxxx)` };
    }
    const corp = stripDigits(r.corpRegNo);
    if (corp && corp.length !== 13) {
      return { ok: false, message: `기관코드 ${code}: 법인등록번호는 숫자 13자리입니다. (xxxxxx-xxxxxxx)` };
    }
    const fy = String(r.foundedYear || '').trim();
    if (fy && !/^(19|20)\d{2}$/.test(fy)) {
      return { ok: false, message: `기관코드 ${code}: 설립연도는 4자리 연도로 입력하세요.` };
    }
  }
  return { ok: true };
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
